import {
    forwardRef,
    HTMLAttributes,
    Ref,
    useMemo,
    useState,
} from "react";
import {
    unstable_useTreeItem2 as useTreeItem2,
    UseTreeItem2Parameters,
} from '@mui/x-tree-view/useTreeItem2';
import {
    TreeItem2Content,
    TreeItem2GroupTransition,
    TreeItem2Root,
} from '@mui/x-tree-view/TreeItem2';
import { TreeItem2Provider } from '@mui/x-tree-view/TreeItem2Provider';
import { Box, Chip, ToggleButtonGroup, ToggleButton, FormHelperText, Stack, styled, Tooltip, IconButton } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { adwUpdateSelectedFile, selectAdvancedFiles, selectFileSummary } from "../../dataWranglerSlice";
import { isFileSelected, setWithPath, getWithPath, replaceWithConfig } from "../../UtilityFunctions";
import LassoSwitch from "../../../../components/form/LassoSwitch";
import LassoCheckbox from "../../../../components/form/LassoCheckbox";
import { colours } from "../../../../StyleConstants";
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';

enum ImagingType {
    Raw = "raw",
    Derivatives = "derivatives",
    Source = "source",
}

const CustomTreeItemContent = styled(TreeItem2Content)(({ theme }) => ({
    padding: theme.spacing(0),
}));

interface CustomTreeItemProps
    extends Omit<UseTreeItem2Parameters, 'rootRef'>,
        Omit<HTMLAttributes<HTMLLIElement>, 'onFocus'> {}

const CustomCheckbox = forwardRef(function CustomCheckbox(
    props: any,
    ref: React.Ref<HTMLInputElement>,
) {
    // Fix warning Received `true` for a non-boolean attribute `visible`.
    const _props = { ...props, visible: props.visible?.toString() || "false" };
    const isSingleValue = props.item?.children?.length === 0;
    if (props.item.type === 'file_suffix') {
        return (
            <LassoCheckbox
                // @ts-ignore
                ref={ref}
                {..._props}
                tabIndex={0}
            />
        )
    } else if (props.item.type !== 'entity') {
        return (
            <LassoSwitch
                disabled={isSingleValue}
                // @ts-ignore
                ref={ref}
                {..._props}
                tabIndex={0}
            />
        )
    } else {
        return <></>
    }
});

const ADWFileTypeOption = forwardRef(
    function ADWFileTypeOption(props: CustomTreeItemProps & { selectedFormat?: string }, ref: Ref<HTMLLIElement>) {
        const { id, itemId, label, disabled, children, selectedFormat, ...other } = props;
        const {
            getRootProps,
            getContentProps,
            getCheckboxProps,
            getGroupTransitionProps,
            publicAPI,
        } = useTreeItem2({ id, itemId, children, label, disabled, rootRef: ref });
        // @ts-ignore
        const item = publicAPI.getItem(itemId);
        const selectedFiles = useSelector(selectAdvancedFiles);
        const dispatch = useDispatch();
        const isSelected: boolean = isFileSelected(selectedFiles ?? {}, item);
        const fileTransferSummary = useSelector(selectFileSummary);
        const [entities, setEntities] = useState<string[]>(item.entityValues);
        const selectedFormatArray = selectedFormat?.split(',');

        const setFileEntities = (newEntities: string[]) => {
            const copy = JSON.parse(JSON.stringify(selectedFiles));
            // If all entities selected, set the entity filter to true
            if (item.entityValues.length === newEntities.length) {
                setWithPath(copy, item.selectionPath, true);
            } else {
                setWithPath(copy, item.selectionPath, newEntities);
            }
            setEntities(newEntities);
            dispatch(adwUpdateSelectedFile(copy));
        }

        const addSelectedFiles = () => {
            const copy = JSON.parse(JSON.stringify(selectedFiles));

            // Recursively select the children files
            const children = [item];
            while(children.length) {
                const child = children.shift();
                if (child.children?.length > 0) {
                    // push new children to the queue
                    children.push(...child.children);
                } else {
                    child.selectionPath && setWithPath(copy, child.selectionPath, true);
                    child?.altPaths?.map((path: []) => {
                        setWithPath(copy, path, true)
                    })
                }
            }

            dispatch(adwUpdateSelectedFile(copy));
        }

        const removeSelectedFiles = () => {
            const copy = JSON.parse(JSON.stringify(selectedFiles));

            // Recursively delete the children files
            const children = [item];
            while(children.length) {
                const child = children.shift();
                if (child.children?.length > 0) {
                    children.push(...child.children);
                } else {
                    child.selectionPath && setWithPath(copy, child.selectionPath, null, true);
                    child?.altPaths?.map((path: []) => setWithPath(copy, path, null, true));

                    // Now that child has been deleted,
                    // check if any ancestor is now empty
                    // and needs to be deleted
                    const parentPaths = [
                        child.selectionPath.slice(0, -1),
                        ...(child?.altPaths
                            ? child.altPaths.map((path: any) => path.slice(0, -1))
                            : []
                        )
                    ];

                    parentPaths.forEach(parentPath => {
                        while(parentPaths.length) {
                            const parent = getWithPath(copy, parentPath);
                            if (!parent || typeof parent !== "object") break;
                            if (Object.values(parent).filter((v) => v).length) break;

                            setWithPath(copy, parentPath, null, true);
                            parentPath = parentPath.slice(0, -1);
                        }
                    });
                }
            }

            dispatch(adwUpdateSelectedFile(copy));
        }

        const getPipelineType = () => {
            if (item.selectionPath[1] === 'pipelines') {
                return ImagingType.Derivatives;
            } else {
                return ImagingType.Raw;
            }
        }

        const optionFileCount = useMemo(() => {
            if (!isSelected || item.type !== 'file_suffix') return null;
            let countData = null;
            const suffix = item.label === '[no_suffix]' ? null : item.label;

            switch (getPipelineType()) {
            case ImagingType.Derivatives:
                countData = fileTransferSummary.derivativeCountDetails
                    ?.find((row: any) => {
                        return (
                            row['pipeline'] === item.selectionPath?.[2]
                            && row['suffix'] === suffix
                            && JSON.stringify(row['groups']) === JSON.stringify(item.groups)
                        );
                    })?.count;
                break;
            case ImagingType.Raw: {
                const dataType = [item.selectionPath.slice(-2,-1), suffix].join('|');
                countData = fileTransferSummary.fileCountDetails[dataType];
            }
            }

            return countData || 0;
        }, [fileTransferSummary]);

        const labelWithCount =
            <>
                {label}
                {item?.formats
                    ?.filter(Boolean)
                    ?.map((format: string) => {
                        const formatEnabled =
                            ! selectedFormat
                            || selectedFormat === 'all'
                            || selectedFormat === format
                            // if loop format is not in the list of filtered format 
                            // don't exclude anything
                            || selectedFormatArray?.includes(format)
                            // if selected format is not an item format
                            // don't exclude anything 
                            || !item.formats?.filter((format: string) => selectedFormatArray?.includes(format)).length
                        return (
                            <Chip
                                key={format}
                                sx={{
                                    backgroundColor: formatEnabled ? colours.notifications.info20 : '#eee',
                                    color: formatEnabled ? colours.notifications.info : '#bbb',
                                    height: 18,
                                    borderRadius: 24,
                                    fontSize: 10,
                                    borderWidth: 0,
                                    fontWeight: "600",
                                    ml: 2,
                                }}
                                label={format}
                            />
                        );
                    })
                }
                {optionFileCount ?
                    <Tooltip title={`${optionFileCount || 0} files`} placement="right">
                        <Chip
                            label={optionFileCount || 0}
                            color="primary"
                            sx={{
                                height: 18,
                                borderRadius: 24,
                                fontSize: 10,
                                borderWidth: 0,
                                fontWeight: "600",
                                opacity: 0.5,
                                ml: 2
                            }}
                        />
                    </Tooltip> :
                    <></>
                }
            </>

        const renderItem = () => {
            switch (item?.type) {
            case 'entity':
                return (
                    <Box pl="20px">
                        <FormHelperText>{item.label}</FormHelperText>

                        <ToggleButtonGroup
                            sx={{ flexWrap: "wrap" }}
                            color="primary"
                            value={entities}
                            onChange={(
                                event: React.MouseEvent<HTMLElement>,
                                newEntities: string[],
                            ) => {
                                setFileEntities(newEntities);
                            }}
                        >
                            {item.entityValues.map((label: string, i: number) => {
                                return <ToggleButton
                                    key={`toogle-button-${i}`}
                                    size="small"
                                    value={label}
                                    sx={{ px: 1, py: 0,
                                        '&:hover': {
                                            background: '#FFFDE7'
                                        },
                                    }}
                                    // Prevents default RichTreeView behavior of expanding/collapsing elements
                                    onKeyDown={(e) => e.stopPropagation()}
                                >{label}</ToggleButton>
                            }
                            )}
                        </ToggleButtonGroup>
                    </Box>
                );
            case 'file_suffix':
                return (
                    <Stack sx={{ py: 1 }}>
                        <Stack direction="row" alignItems="center" style={{ fontSize: '14px' }}>
                            {labelWithCount}
                            {item?.description &&
                                        <Stack direction="row">
                                            {item?.description?.data_description &&
                                                <Tooltip
                                                    title={
                                                        <span dangerouslySetInnerHTML={{
                                                            __html : item.description.data_description
                                                        }}/>
                                                    }
                                                    arrow placement="right"
                                                >
                                                    <IconButton size="small">
                                                        <InfoIcon fontSize="small" style={{ color: "#ccc" }}/>
                                                    </IconButton>
                                                </Tooltip>
                                            }
                                            {item?.description?.issues_description &&
                                                <Box>
                                                    <Tooltip
                                                        title={
                                                            <span dangerouslySetInnerHTML={{
                                                                __html : replaceWithConfig(item.description.issues_description)
                                                            }}/>
                                                        }
                                                        arrow placement="right"
                                                    >
                                                        <IconButton size="small">
                                                            <WarningIcon fontSize="small" style={{ color: colours.orange.orange }}/>
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            }
                                        </Stack>
                            }
                        </Stack>
                        <small
                            style={{ fontSize: '12px', color: '#999' }}
                            dangerouslySetInnerHTML={{
                                __html : item?.humanReadableLabel
                            }}
                        ></small>
                    </Stack>
                );
            default:
                return (
                    <>
                        {item?.humanReadableLabel || label}
                        {item?.category &&
                                <Chip
                                    label={item?.category}
                                    sx={{
                                        backgroundColor: colours.orange.orange20,
                                        color: colours.orange.orange,
                                        height: 18,
                                        borderRadius: 24,
                                        fontSize: 10,
                                        borderWidth: 0,
                                        fontWeight: "600",
                                        ml: 2,
                                    }}
                                />
                        }
                        {item?.description &&
                                <Stack direction="row">
                                    {item?.description?.data_description &&
                                        <Tooltip
                                            title={
                                                <span dangerouslySetInnerHTML={{
                                                    __html : item.description.data_description
                                                }}/>
                                            }
                                            arrow placement="right"
                                        >
                                            <IconButton size="small">
                                                <InfoIcon fontSize="small" style={{ color: "#ccc" }}/>
                                            </IconButton>
                                        </Tooltip>
                                    }
                                    {item?.description?.issues_description &&
                                        <Box>
                                            <Tooltip
                                                title={
                                                    <span dangerouslySetInnerHTML={{
                                                        __html : replaceWithConfig(item.description.issues_description)
                                                    }}/>
                                                }
                                                arrow placement="right"
                                            >
                                                <IconButton size="small">
                                                    <WarningIcon fontSize="small" style={{ color: colours.orange.orange }}/>
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    }
                                </Stack>
                        }
                    </>
                );
            }};

        return (
            // @ts-ignore
            <TreeItem2Provider itemId={itemId}>
                <TreeItem2Root {...getRootProps(other)}>
                    <CustomTreeItemContent {...getContentProps()} >
                        <CustomCheckbox
                            {...getCheckboxProps()}
                            onChange={(event: any) => {
                                const { onChange } = getCheckboxProps();
                                event.target.checked ?
                                    addSelectedFiles():
                                    removeSelectedFiles()
                                onChange(event);
                            }}
                            onKeyDown={(event: KeyboardEvent) => {
                                const inputElement = event.target as HTMLInputElement
                                if(event.key === " ") {
                                    const { onChange } = getCheckboxProps();
                                    !inputElement.checked ?
                                        addSelectedFiles():
                                        removeSelectedFiles();
                                    //@ts-ignore
                                    onChange(event);
                                }
                            }}
                            item={item}
                            checked={isSelected}
                        />
                        {/*<TreeItem2Checkbox {...getCheckboxProps()} />*/}
                        {renderItem()}
                        {/*<TreeItem2Label {...getLabelProps()} />*/}
                    </CustomTreeItemContent>
                    {children && <TreeItem2GroupTransition {...getGroupTransitionProps()} />}
                </TreeItem2Root>
            </TreeItem2Provider>
        );
    }
);

export default ADWFileTypeOption;